<template>
  <!-- Chat Avatar - Aparece em todas as telas -->
  <div v-if="showChat">
    <!-- Etapas 1-2: Avatar no canto inferior esquerdo (desktop) / centralizado no rodapé (mobile) -->
    <div
      v-if="currentStep === 1 || currentStep === 2"
      class="chat-avatar-container-bottom"
      :class="{ 'chat-avatar-clickable': currentStep < 4 }"
      @click="nextStep"
    >
      <!-- Balão de fala -->
      <div
        class="chat-speech-bubble"
        :class="{
          'speech-bubble-step-1': currentStep === 1,
          'speech-bubble-step-2': currentStep === 2,
        }"
      >
        <div class="speech-bubble-content">
          <div v-if="currentStep === 1">
            <p class="mb-1">
              Olá, {{ userName }}! Boas-vindas!
            </p>
            <p class="mb-0">
              Sou a Marina e estou aqui para te ajudar nessa jornada.
            </p>
          </div>
          <div v-if="currentStep === 2">
            <p class="mb-0">
              Se eu estiver atrapalhando, é só clicar em mim que eu me escondo.
              Mas, se precisar de ajuda, é só me chamar ali no topo, tá bom?
            </p>
          </div>
        </div>
        <!-- Triângulo apontando para o avatar -->
        <div class="speech-bubble-pointer-bottom"></div>
      </div>

      <!-- Avatar -->
      <div class="chat-avatar-image">
        <img
          v-if="currentStep === 1"
          src="/img/avatar-real-body.png"
          alt="Marina"
          class="avatar-img"
          :style="{ width: avatarSizeStep1 }"
        />
        <img
          v-if="currentStep === 2"
          src="/img/avatar-real-body.png"
          alt="Marina"
          class="avatar-img"
          :style="{ width: avatarSizeStep2 }"
        />
      </div>
    </div>

    <!-- Etapas 3-4: Avatar centralizado no topo -->
    <div
      v-if="currentStep === 3 || currentStep === 4"
      class="chat-avatar-container-top"
      :class="{ 'chat-avatar-clickable': currentStep === 3 || currentStep === 4 }"
      @click="toggleChatInterface"
    >
      <!-- Avatar -->
      <div class="chat-avatar-image-top">
        <img
          v-if="currentStep === 3"
          src="/img/avatar-real-navbar.png"
          alt="Marina"
          class="avatar-img-top"
        />
        <img
          v-if="currentStep === 4"
          src="/img/avatar-real-navbar.png"
          alt="Marina"
          class="avatar-img-top"
        />
      </div>

      <!-- Balão de fala - Apenas no passo 3, abaixo do avatar -->
      <div
        v-if="currentStep === 3"
        class="chat-speech-bubble-top-middle"
      >
        <div class="speech-bubble-content">
          <p class="mb-0">
            Quando precisar, estarei aqui!
          </p>
        </div>
        <!-- Triângulo apontando para cima (para o avatar) -->
        <div class="speech-bubble-pointer-top-middle"></div>
      </div>
    </div>

    <!-- Chat Interface -->
    <ChatInterface
      :is-open="chatInterfaceOpen"
      :user-name="userName"
      @close-chat="chatInterfaceOpen = false"
    />
  </div>
</template>

<script>
import ChatInterface from "./ChatInterface.vue";
import { EventBus } from "@/utils/EventBus";

export default {
  name: "ChatAvatar",
  components: {
    ChatInterface,
  },
  props: {
    userName: {
      type: String,
      default: "",
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentStep: 1,
      showChat: false,
      chatInterfaceOpen: false,
      autoStepTimer: null,
      debtsLoaded: false, // Flag para saber se as dívidas já foram carregadas
      hasAmigavelDebts: false, // Flag para saber se o cliente tem dívidas amigáveis (credor 2 ou 7) OU agreements com credor 2 ou 7
      // ========== ALTERE OS TAMANHOS AQUI PARA TESTAR ==========
      avatarSizeStep1: '260px', // Tamanho do avatar no PASSO 1
      avatarSizeStep2: '260px', // Tamanho do avatar no PASSO 2
      // =========================================================
      // DEV FLAG: manter false para desativar avanço automático dos passos.
      // Para voltar ao comportamento anterior, altere para true.
      enableAutoStep: true,
      stepTimers: {
        1: 5000, // 5 segundos para ler "Olá, [nome]! Boas-vindas! Sou a Marina..."
        2: 8000, // 8 segundos para ler o texto do passo 2 "Se eu estiver atrapalhando..."
        3: 5000, // 5 segundos para ler "Quando precisar, estarei aqui!"
      },
    };
  },
  watch: {
    show(newVal) {
      if (newVal && this.debtsLoaded && !this.hasAmigavelDebts) {
        this.showChat = false;
        return;
      }
      this.showChat = newVal;
      if (newVal) {
        this.currentStep = this.getStepsAlreadySeen() ? 4 : 1;
        if (this.currentStep === 1 && this.debtsLoaded && this.hasAmigavelDebts) {
          this.startAutoStep();
        }
      } else {
        this.clearAutoStepTimer();
      }
    },
    debtsLoaded(newVal) {
      if (newVal && this.showChat && this.hasAmigavelDebts) {
        this.currentStep = this.getStepsAlreadySeen() ? 4 : 1;
        if (this.currentStep === 1) {
          this.startAutoStep();
        }
      }
    },
    currentStep(newStep) {
      // Quando o passo muda, reinicia o timer automático (só se ainda não terminou os passos)
      this.clearAutoStepTimer();
      if (newStep < 4 && this.showChat && this.hasAmigavelDebts) {
        this.startAutoStep();
      }
    },
  },
  mounted() {
    this.showChat = false;
    this.currentStep = this.getStepsAlreadySeen() ? 4 : 1;
    EventBus.$on("debts-loaded", this.onDebtsLoaded);
  },
  beforeDestroy() {
    this.clearAutoStepTimer();
    // Remove o listener do evento
    EventBus.$off("debts-loaded", this.onDebtsLoaded);
  },
  methods: {
    onDebtsLoaded(data) {
      // Marca que as dívidas foram carregadas
      this.debtsLoaded = true;
      
      // Verifica se o cliente tem dívidas amigáveis (credor 2 ou 7) OU agreements com credor 2 ou 7
      // O ChatAvatar só deve ser exibido para clientes amigáveis (debts ou agreements)
      if (data && typeof data.hasAmigavelDebts !== 'undefined') {
        this.hasAmigavelDebts = data.hasAmigavelDebts;
      }
      
      if (this.hasAmigavelDebts && this.show) {
        this.showChat = true;
        this.currentStep = this.getStepsAlreadySeen() ? 4 : 1;
        if (this.currentStep === 1) {
          this.startAutoStep();
        }
      } else {
        this.showChat = false;
      }
    },
    getStepsAlreadySeen() {
      try {
        return localStorage.getItem("chatAvatarStepsSeen") === "true";
      } catch {
        return false;
      }
    },
    setStepsSeen() {
      try {
        localStorage.setItem("chatAvatarStepsSeen", "true");
      } catch (_) {
        // Storage indisponível (ex.: modo anônimo)
      }
    },
    startAutoStep() {
      if (!this.enableAutoStep) {
        return;
      }
      // Limpa qualquer timer existente
      this.clearAutoStepTimer();
      
      // Se já está no último passo, não precisa de timer
      if (this.currentStep >= 4) {
        return;
      }

      // Só inicia se as dívidas já foram carregadas E se o cliente tem dívidas amigáveis OU agreements amigáveis
      if (!this.debtsLoaded || !this.hasAmigavelDebts) {
        return;
      }

      // Define o tempo baseado no passo atual
      const delay = this.stepTimers[this.currentStep] || 5000;
      
      // Cria o timer para avançar automaticamente
      this.autoStepTimer = setTimeout(() => {
        this.nextStep();
      }, delay);
    },
    clearAutoStepTimer() {
      if (this.autoStepTimer) {
        clearTimeout(this.autoStepTimer);
        this.autoStepTimer = null;
      }
    },
    nextStep() {
      this.clearAutoStepTimer();
      if (this.currentStep < 4) {
        this.currentStep++;
        if (this.currentStep === 4) {
          this.setStepsSeen();
          this.$emit("chat-completed");
        }
      }
    },
    hideChat() {
      this.showChat = false;
      this.$emit("chat-hidden");
    },
    toggleChatInterface() {
      if (this.currentStep === 4) {
        // No passo 4, abre/fecha o chat
        this.chatInterfaceOpen = !this.chatInterfaceOpen;
        this.$emit("toggle-chat", this.chatInterfaceOpen);
        // Emite evento para salvar log do clique no avatar
        this.$emit("avatar-clicked");
      } else if (this.currentStep === 3) {
        // No passo 3, avança para o passo 4
        this.nextStep();
      }
    },
  },
};
</script>

<style scoped>
/* Etapas 1-2: Avatar à esquerda, balão à direita sobrepondo um pouco */
.chat-avatar-container-bottom {
  position: fixed;
  bottom: 0px;
  left: 10px;
  z-index: 9999;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
}

.chat-avatar-clickable {
  cursor: pointer;
}

.chat-avatar-image {
  position: relative;
  z-index: 10001;
  flex-shrink: 0;
  order: 1; /* Avatar primeiro (esquerda) */
  transform: translateY(50px);
}

.avatar-img {
  width: 120px;
  height: auto;
  max-width: 100%;
  display: block;
}

.chat-speech-bubble {
  position: relative;
  background-color: #e3f2fd;
  border-radius: 15px;
  padding: 12px 15px;
  margin-left: 10px;
  margin-bottom: 130px; /* Subiu 50px */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  max-width: 280px;
  z-index: 10000;
  order: 2; /* Balão depois (direita) */
}

.speech-bubble-step-1 {
  min-width: 220px;
}

.speech-bubble-step-2 {
  min-width: 280px;
}

.speech-bubble-content {
  color: #1976d2;
  font-size: 12px;
  line-height: 1.4;
  text-align: left;
}

.speech-bubble-content p {
  margin-bottom: 4px;
  font-weight: 500;
}

/* Ponteiro apontando para a esquerda (para o avatar) */
.speech-bubble-pointer-bottom {
  position: absolute;
  left: -10px;
  bottom: 20px;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-right: 10px solid #e3f2fd;
}

/* Etapas 3-4: Avatar centralizado no topo */
.chat-avatar-container-top {
  position: fixed;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--chat-avatar-top-z, 1034);
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 350px;
}

.chat-avatar-image-top {
  position: relative;
  z-index: var(--chat-avatar-top-z, 1034);
  order: 1;
}

.avatar-img-top {
  width: 80px;
  height: auto;
  max-width: 100%;
  display: block;
  /* Sombra no bottom - sensação de escondidinho */
  filter: drop-shadow(0 6px 4px rgba(0, 0, 0, 0.4));
}

.chat-speech-bubble-top-middle {
  position: relative;
  background-color: #e3f2fd;
  border-radius: 15px;
  padding: 12px 15px;
  margin-top: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  max-width: 280px;
  order: 2;
  z-index: 10000;
}

.speech-bubble-pointer-top-middle {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid #e3f2fd;
}

/* Responsividade */
@media (max-width: 500px) {
  /* Etapas 1-2: Avatar à esquerda, balão à direita (mobile) */
  .chat-avatar-container-bottom {
    bottom: 0px;
    left: 10px;
    right: 10px;
    width: calc(100% - 20px);
    transform: none;
    flex-direction: row;
    align-items: flex-end;
    box-sizing: border-box;
  }

  .chat-avatar-image {
    flex-shrink: 0;
    order: 1; /* Avatar à esquerda */
    overflow: hidden;
    height: 250px;
    width: 35vw;
    max-width: 140px;
    transform: translateY(50);
  }

  .avatar-img {
    width: 100%;
    height: 300px;
    object-fit: contain;
    object-position: center top;
  }

  .chat-speech-bubble {
    order: 2; /* Balão à direita */
    min-width: 0;
    flex: 1;
    max-width: none;
    width: auto;
    padding: 10px 12px;
    margin-left: 10px;
    margin-right: 0;
    margin-bottom: 80px;
  }

  .speech-bubble-step-1 {
    min-width: 150px;
  }

  .speech-bubble-step-2 {
    min-width: 150px;
  }

  /* Ponteiro apontando para a esquerda (para o avatar) */
  .speech-bubble-pointer-bottom {
    left: -10px;
    bottom: 15px;
    transform: none;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid #e3f2fd;
    border-left: none;
  }

  /* Etapas 3-4: Posiciona entre "Negocie Porto Bank" e o nome do usuário no mobile */
  .chat-avatar-container-top {
    top: 4px; /* Ajusta verticalmente no navbar */
    left: calc(50% + 90px); /* Posiciona à direita do centro, entre "Negocie Porto Bank" e o nome */
    transform: translateX(-50%); /* Centraliza o avatar em relação ao seu próprio centro */
    position: fixed;
    z-index: var(--chat-avatar-top-z, 1034); /* Controlado dinamicamente pelo layout */
  }

  .avatar-img-top {
    width: 70px; /* Menor no mobile para não ocupar muito espaço */
  }

  .chat-speech-bubble-top-middle {
    min-width: 180px;
    max-width: 240px;
    padding: 8px 12px;
  }

  .speech-bubble-content {
    font-size: 11px;
  }
}
</style>

